import { StoryblokVersion } from '~/utils/storyblok-constants';
import { useStory } from './useStory';
import type { RouteLocationNormalizedGeneric } from 'vue-router';

/**
 * Fetches the configuration for the current section from Storyblok.
 *
 * For fetching any single arbitrary story, use `useStory`. For fetching the global config, use `useGlobalConfig`. For
 * fetching multiple stories, use `useStories`. For fetching datasource entries, use `useDatasourceEntries`.
 *
 * **Example:**
 * ```ts
 * import { useConfig } from '~/composables/storyblok-proxy/useConfig';
 *
 * const { data: story } = useConfig();
 * useStoryblokBridgeRef(story);
 * ```
 *
 * @returns A reactive object containing the fetched config story data, loading state, and error state.
 * @throws Will throw an fatal error if the fetch fails.
 */
export const useConfig = (to?: RouteLocationNormalizedGeneric) => {
    const matchedConfig = useCurrentSectionConfig(to);
    const { lang, country } = useLanguage(to);
    const { isDraftModeEnabled } = useDraftMode();

    const slug = computed(() => (matchedConfig.value ? `${matchedConfig.value.slug}/config` : 'config'));

    const key = computed(
        () =>
            `config:${slug.value}:${country.value}:${lang.value}:${isDraftModeEnabled.value ? StoryblokVersion.Draft : StoryblokVersion.Published}`
    );

    return useStory<ConfigStoryblok>(
        slug,
        { resolve_links: 'link', resolve_relations: 'config.postLoginDefaultStory,config.lockedPagesFallbackStory' },
        {
            key,
            enrichLinkedStories: 'locked',
            includeSectionConfig: false,
            route: to
        }
    );
};
